import { DataVolume, Entity, FieldType, PersistenceType, ReferenceField } from 'gossamer-unified';

export interface TaskDefinitionRow {
  id: number;
  seq: number;
  type: string;
  label: string;
  institution_id: number;
  parent_definition_id: number;
  markdown_text: string;
}

export const TaskDefinition: Entity<TaskDefinitionRow> = {
  id: 'TaskDefinition',
  title: 'Task Definition',
  service: 'main',
  rowLabel: (row: TaskDefinitionRow) => row.label,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.task_definition',
  },
  security: [
    { role: 'staff', action: 'all', access: 'all' },
    {
      role: 'all-authed',
      action: 'read',
      access: 'conditional',
      condition: 'institution_id IN (300, {accessInstitutionIds})',
    },
    {
      role: 'admin',
      action: 'all',
      access: 'conditional',
      condition: 'institution_id IN (300, {accessInstitutionIds})',
    },
  ],
  defaultSort: [['id', 'DESC']],
  defaultFilters: [
    { fieldId: 'institution_id', operator: 'eq', value: '' },
    { fieldId: 'type', operator: 'eq', value: 'ROOT', display: 'hidden' },
    { fieldId: 'label', operator: 'ct', value: '' },
  ],
  listRowLink: '/admin/workflow/display/{key}',
  refOrderClause: 'SELECT ZZ.label FROM public.task_definition ZZ WHERE ZZ.id',
  dataVolume: DataVolume.Large,
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'Id',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
      autoIncrement: true,
    },
    seq: {
      seq: 10,
      type: FieldType.Integer,
      label: 'Sequence',
    },
    type: {
      seq: 15,
      type: FieldType.Options,
      label: 'Type',
      mandatory: true,
      options: [
        {
          id: 'ROOT',
          label: 'Root (Sequential)',
        },
        {
          id: 'PARALLEL-GROUP',
          label: 'Parallel Group',
        },
        {
          id: 'SEQUENTIAL-GROUP',
          label: 'Sequential Group',
        },
        {
          id: 'APPLICATION-TASK',
          label: 'Application Task',
        },
        {
          id: 'CHECK-TASK',
          label: 'Check Task',
        },
      ],
    },
    label: {
      seq: 20,
      type: FieldType.Text,
      label: 'Label',
      mandatory: true,
      listColumn: true,
      maxLength: 255,
    },
    institution_id: {
      seq: 22,
      type: FieldType.Integer,
      label: 'Institution Id',
    },
    parent_definition_id: {
      seq: 25,
      type: FieldType.Integer,
      label: 'Parent Definition Id',
    },
    markdown_text: {
      seq: 30,
      type: FieldType.Richtext,
      label: 'Markdown Text',
      editorHeight: 300,
    },
  },
};

// Needs to be separated as includes reference to self
TaskDefinition.fields.parent_definition_id.type = FieldType.Reference;
(TaskDefinition.fields.parent_definition_id as ReferenceField).refEntity = TaskDefinition;

export const WORKFLOW_ROOT_TYPE = 'ROOT';
export const isRoot = (type: string) => type === WORKFLOW_ROOT_TYPE;
export const WORKFLOW_GROUP_TYPES = [WORKFLOW_ROOT_TYPE, 'SEQUENTIAL-GROUP', 'PARALLEL-GROUP'];
export const isGroup = (type: string) => WORKFLOW_GROUP_TYPES.includes(type);
export const isSequentialGroup = (type: string) => [WORKFLOW_ROOT_TYPE, 'SEQUENTIAL-GROUP'].includes(type);
export const isParallelGroup = (type: string) => type === 'PARALLEL-GROUP';
export const INACTIVE_TASK_STATUSES = ['COMPLETED', 'SKIPPED'];
